import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import diagnosisApi from '@/api/survey/main';
import { getDignosisData, getValueType } from '@/common/utils';
import {
  DiagnosisState,
  SurveyResponse,
  ApiResponse,
  SaveSurveyPayload,
  SetDiagnosisFormDataPayload,
  SetLegalFormDataPayload,
  SetBasicInfoPayload,
} from '../interface';

// Async thunks
export const saveDiagnosisFormData = createAsyncThunk(
  'diagnosis/saveDiagnosisFormData',
  async (_, { rejectWithValue, getState }) => {
    try {
      const isAccept = window.localStorage.getItem('isAccept');
      let answer: SurveyResponse[] = [];

      if (isAccept && isAccept === '1') {
        answer = getDignosisData('diagnosis');
      } else {
        const state = getState() as { diagnosis: DiagnosisState };
        answer = getValueType(state.diagnosis.diagnosisFormData, 'diagnosis');
      }

      const res: ApiResponse = await diagnosisApi.saveDiagnosis({ answer });
      if (res.data.code === 201) {
        return res.data;
      } else {
        return rejectWithValue(res);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveLegalFormData = createAsyncThunk(
  'diagnosis/saveLegalFormData',
  async (_, { rejectWithValue, getState }) => {
    try {
      const isAccept = window.localStorage.getItem('isAccept');
      let answer: SurveyResponse[] = [];

      if (isAccept && isAccept === '1') {
        answer = getDignosisData('leaglForm');
      } else {
        const state = getState() as { diagnosis: DiagnosisState };
        answer = getValueType(state.diagnosis.leagalFormData, 'leaglForm');
      }

      const res: ApiResponse = await diagnosisApi.saveLegal({ answer });
      if (res.data.code === 201) {
        return res.data;
      } else {
        return rejectWithValue(res);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveSurveyData = createAsyncThunk(
  'diagnosis/saveSurveyData',
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const isAccept = window.localStorage.getItem('isAccept');
      let diagnosisData: SurveyResponse[] = [];
      let legalFormData: SurveyResponse[] = [];

      if (isAccept === '1') {
        console.log('🔍 saveSurveyData: Using localStorage path (isAccept === "1")');
        diagnosisData = getDignosisData('diagnosis');
        legalFormData = getDignosisData('leagalForm');
      } else {
        console.log('🔍 saveSurveyData: Using Redux store path (isAccept !== "1")');
        const state = getState() as { diagnosis: DiagnosisState };
        console.log('🔍 Current diagnosis state:', state.diagnosis);
        diagnosisData = getValueType(state.diagnosis.diagnosisFormData, 'diagnosis');
        legalFormData = getValueType(state.diagnosis.leagalFormData, 'leagalForm');
      }

      console.log('🔍 saveSurveyData: diagnosisData.length =', diagnosisData.length);
      console.log('🔍 saveSurveyData: legalFormData.length =', legalFormData.length);

      if (diagnosisData.length && legalFormData.length) {
        const payload: SaveSurveyPayload = {
          diagnosis: diagnosisData,
          legal: legalFormData,
        };

        const res: ApiResponse = await diagnosisApi.save(payload);

        if (res.data.code === 201) {
          // Clear form data after successful save
          dispatch(removeDiagnosisFormData());
          dispatch(removeLegalFormData());
          return res.data;
        } else {
          return rejectWithValue(res);
        }
      }

      return rejectWithValue('Missing form data');
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Initial state
const initialState: DiagnosisState = {
  basicInfo: {
    safeSavingMonthRatio: 0,
    maximumDebtRatio: 0,
    currentLoanInterestRate: 0,
    loanTerm: 0,
    minimumBorrowingCapacityInvestment: 0,
  },
  diagnosisFormData: [],
  leagalFormData: [],
  loading: false,
  error: null,
};

// Slice
export const diagnosisSlice = createSlice({
  name: 'diagnosis',
  initialState,
  reducers: {
    setDiagnosisFormData: (state, action: PayloadAction<SetDiagnosisFormDataPayload>) => {
      console.log('🔍 setDiagnosisFormData called with payload:', action.payload);
      state.diagnosisFormData = action.payload.diagnosisFormData || [];
      console.log('🔍 Updated diagnosisFormData in store:', state.diagnosisFormData);
    },

    setLegalFormData: (state, action: PayloadAction<SetLegalFormDataPayload>) => {
      state.leagalFormData = action.payload.leagalFormData || [];
    },

    setBasicInfo: (state, action: PayloadAction<SetBasicInfoPayload>) => {
      const basicInfo = action.payload.basicInfo;
      state.basicInfo = {
        ...state.basicInfo,
        ...basicInfo,
        // Map snake_case API fields to camelCase
        safeSavingMonthRatio: basicInfo.safe_saving_month_ratio || basicInfo.safeSavingMonthRatio || 0,
        maximumDebtRatio: basicInfo.maximum_debt_ratio || basicInfo.maximumDebtRatio || 0,
        currentLoanInterestRate: basicInfo.current_loan_interest_rate || basicInfo.currentLoanInterestRate || 0,
        loanTerm: basicInfo.loan_term || basicInfo.loanTerm || 0,
        minimumBorrowingCapacityInvestment: basicInfo.minimum_borrowing_capacity_investment || basicInfo.minimumBorrowingCapacityInvestment || 0,
      };
    },

    removeDiagnosisFormData: (state) => {
      // Clear localStorage
      window.localStorage.removeItem('diagnosisData');
      window.localStorage.removeItem('diagnosisFormDataIsEnd');
      window.localStorage.removeItem('curQuestionId');
      // Clear state
      state.diagnosisFormData = [];
    },

    removeLegalFormData: (state) => {
      // Clear localStorage
      window.localStorage.removeItem('leagalFormData');
      window.localStorage.removeItem('leagalFormDataIsEnd');
      window.localStorage.removeItem('curLeaglFormQuestionId');
      // Clear state
      state.leagalFormData = [];
    },

    clearError: (state) => {
      state.error = null;
    },

    resetDiagnosisState: () => {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    // Save diagnosis form data
    builder
      .addCase(saveDiagnosisFormData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveDiagnosisFormData.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(saveDiagnosisFormData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Save legal form data
    builder
      .addCase(saveLegalFormData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveLegalFormData.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(saveLegalFormData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Save survey data
    builder
      .addCase(saveSurveyData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveSurveyData.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(saveSurveyData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setDiagnosisFormData,
  setLegalFormData,
  setBasicInfo,
  removeDiagnosisFormData,
  removeLegalFormData,
  clearError,
  resetDiagnosisState,
} = diagnosisSlice.actions;

export default diagnosisSlice.reducer;